import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';

import { ScheduleApiModel, DateRangeApiModel } from '@app/api/api-models';
import { ProgramUserListApiModel } from '@app/api/api-models/users/program-users-list/ProgramUserListApiModel';
import { ProgramRoleDateRanges } from '@app/shared/models/program-role-date-ranges.model';
import { ProgramRoleScheduleList } from '@app/shared/models/program-role-schedule-list.model';
import { ActiveUsers } from '@app/shared/models/active-users.model';
import { ProgramRole } from '@app/shared/enums/program-role.enum';
import { ScheduleService } from '@app/api/api-services/schedule.service';

@Injectable()
export class ScheduleCachedService {
  private _scheduleList: Array<ProgramRoleScheduleList> = [];
  private _forbiddenDates: Array<ProgramRoleDateRanges> = [];
  private _activeUsers: Array<ActiveUsers> = [];

  constructor(private readonly scheduleService: ScheduleService) {}

  public getActiveUsers(role: ProgramRole): Observable<Array<ProgramUserListApiModel>> {
    const targetActiveUsers = this._activeUsers.find((item) => item.role === role);
    if (targetActiveUsers) {
      return of(targetActiveUsers.users);
    }

    return this.scheduleService.getActiveUsers(role).pipe(
      tap((response) => {
        this._activeUsers.push({
          role: role,
          users: response
        });
      })
    );
  }

  public getScheduleList(targetUserProgramRoleId: string): Observable<Array<ScheduleApiModel>> {
    const targetScheduleList = this._scheduleList.find((item) => item.programRoleId === targetUserProgramRoleId);
    if (targetScheduleList) {
      return of(targetScheduleList.roleScheduleList);
    }

    return this.scheduleService.getByProgramRoleId(targetUserProgramRoleId).pipe(
      tap((response) => {
        this._scheduleList.push({
          programRoleId: targetUserProgramRoleId,
          roleScheduleList: response
        });
      })
    );
  }

  public getForbiddenDates(
    targetUserProgramRoleId: string,
    programRoleIds: string[]
  ): Observable<Array<DateRangeApiModel>> {
    const targetDateRanges = this._forbiddenDates.find((item) => item.programRoleId === targetUserProgramRoleId);
    if (targetDateRanges) {
      return of(targetDateRanges.dateRanges);
    }

    return this.scheduleService.getForbiddenDates(targetUserProgramRoleId, programRoleIds).pipe(
      tap((response) => {
        this._forbiddenDates.push({
          programRoleId: targetUserProgramRoleId,
          dateRanges: response
        });
      })
    );
  }

  public resetTargetSchedule(targetUserProgramRoleId: string): void {
    this._scheduleList = this._scheduleList.filter((item) => item.programRoleId !== targetUserProgramRoleId);
  }

  public resetTargetForbiddenDates(targetUserProgramRoleId: string): void {
    this._forbiddenDates = this._forbiddenDates.filter((item) => item.programRoleId !== targetUserProgramRoleId);
  }

  public resetCache(): void {
    this._scheduleList = [];
    this._forbiddenDates = [];
    this._activeUsers = [];
  }
}
